import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from 'react';
import type { InputsSnapshot, Note, Todo } from '@almanac/contracts';
import { useApi } from '../../api/useApi';
import { useMe } from '../../api/MeContext';
import { useAuth } from '../../auth/AuthContext';
import { ALMANAC_DATA_CHANGED } from '../../lib/data-events';

export type InputComposerKind = 'todo' | 'guidance' | 'research';
export type EditableInput = Todo | Note;

interface InputsContextValue {
  snapshot: InputsSnapshot | null;
  loading: boolean;
  error: string | null;
  railOpen: boolean;
  railPinned: boolean;
  composerKind: InputComposerKind | null;
  editing: EditableInput | null;
  refresh: () => Promise<void>;
  setRailOpen: (open: boolean) => void;
  setRailPinned: (pinned: boolean) => void;
  openComposer: (kind: InputComposerKind, input?: EditableInput) => void;
  closeComposer: () => void;
}

const InputsContext = createContext<InputsContextValue | null>(null);
const PIN_KEY = 'almanac.todoRailPinned';

export function GlobalInputsProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const userId = useMe()?.sub ?? null;
  const { status, sessionVersion } = useAuth();
  const identity = useMemo(
    () => ({ api, userId, status, sessionVersion }),
    [api, userId, status, sessionVersion],
  );
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;
  const [loaded, setLoaded] = useState<{
    identity: typeof identity;
    snapshot: InputsSnapshot | null;
    error: string | null;
  } | null>(null);
  const snapshot = loaded?.identity === identity ? loaded.snapshot : null;
  const error = loaded?.identity === identity ? loaded.error : null;
  const [railPinned, setPinned] = useState(() => localStorage.getItem(PIN_KEY) === 'true');
  const [railOpen, setRailOpen] = useState(() => localStorage.getItem(PIN_KEY) === 'true');
  const [composerKind, setComposerKind] = useState<InputComposerKind | null>(null);
  const [edit, setEdit] = useState<{ identity: typeof identity; input: EditableInput } | null>(
    null,
  );
  const editing = edit?.identity === identity ? edit.input : null;

  const refresh = useCallback(async () => {
    if (status !== 'authenticated') return;
    try {
      const next = await api.listInputs({ includeArchived: true });
      if (currentIdentity.current === identity)
        setLoaded({ identity, snapshot: next, error: null });
    } catch (cause) {
      if (currentIdentity.current === identity)
        setLoaded({
          identity,
          snapshot: null,
          error: cause instanceof Error ? cause.message : String(cause),
        });
    }
  }, [api, status, identity]);

  useEffect(() => {
    void refresh();
    const onChange = () => void refresh();
    window.addEventListener(ALMANAC_DATA_CHANGED, onChange);
    return () => window.removeEventListener(ALMANAC_DATA_CHANGED, onChange);
  }, [refresh]);

  const setRailPinned = useCallback((pinned: boolean) => {
    setPinned(pinned);
    localStorage.setItem(PIN_KEY, String(pinned));
    if (pinned) setRailOpen(true);
  }, []);

  const openComposer = useCallback(
    (kind: InputComposerKind, input?: EditableInput) => {
      setEdit(input ? { identity, input } : null);
      setComposerKind(kind);
    },
    [identity],
  );
  const closeComposer = useCallback(() => {
    setEdit(null);
    setComposerKind(null);
  }, []);

  const value = useMemo<InputsContextValue>(
    () => ({
      snapshot,
      loading: snapshot === null && error === null,
      error,
      railOpen,
      railPinned,
      composerKind,
      editing: snapshot ? editing : null,
      refresh,
      setRailOpen,
      setRailPinned,
      openComposer,
      closeComposer,
    }),
    [
      closeComposer,
      composerKind,
      editing,
      error,
      openComposer,
      railOpen,
      railPinned,
      refresh,
      setRailPinned,
      snapshot,
    ],
  );

  return <InputsContext.Provider value={value}>{children}</InputsContext.Provider>;
}

export function useGlobalInputs(): InputsContextValue {
  const value = useContext(InputsContext);
  if (!value) throw new Error('useGlobalInputs must be used inside GlobalInputsProvider');
  return value;
}
